import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Bell, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function NotificationBell() {
  const [data, setData] = useState({ count: 0, tasks: [] });
  const nav = useNavigate();

  const load = () => api.get("/notifications/due").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(id);
  }, []);

  const dismiss = async (task) => {
    await api.patch(`/tasks/${task.task_id}`, { status: "done" });
    toast.success("Task completed");
    load();
  };
  const snooze = async (task, minutes) => {
    await api.post(`/tasks/${task.task_id}/snooze`, { minutes });
    toast.success("Snoozed");
    load();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="notif-bell" className="relative hover:bg-slate-100">
          <Bell className="w-4 h-4" />
          {data.count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center px-1">
              {data.count > 9 ? "9+" : data.count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <div className="px-3 py-2 border-b border-slate-100">
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Reminders</div>
          <div className="text-sm font-medium">{data.count === 0 ? "You're all caught up" : `${data.count} due now`}</div>
        </div>
        {data.tasks.length === 0 && (
          <div className="px-3 py-6 text-center text-xs text-slate-400">Nothing needs your attention.</div>
        )}
        {data.tasks.map((t) => (
          <div key={t.task_id} className="px-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={`notif-${t.task_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 font-mono-plex">
                  Due {new Date(t.due_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
                {t.priority && <Badge variant="outline" className="mt-1 text-[9px]">{t.priority}</Badge>}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => snooze(t, 15)} title="Snooze 15 min" data-testid={`notif-snooze-${t.task_id}`}>
                  <Clock className="w-4 h-4 text-slate-500" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => dismiss(t)} title="Mark done" data-testid={`notif-done-${t.task_id}`}>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => nav("/tasks")} data-testid="notif-open-tasks">Open all tasks</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
